import { useCallback, useEffect, useState } from 'react'
import type {
  IdentityFieldSummary,
  IdentityWorkspaceMode,
} from '@/types/components/audiences/import-session'
import {
  isIdentityFieldReady,
  resolveInitialWorkspaceMode,
} from '../lib/required-fields-ui'

interface UseRequiredFieldsWorkspaceNavigationArgs {
  sessionId: string
  fields: IdentityFieldSummary[]
  sourcePageSize: number
  initialFieldId?: string | null
  initialWorkspaceMode?: IdentityWorkspaceMode | null
  /** Async: opens the DiscardWorkspaceEditsDialog and resolves to the user's choice. */
  confirmLeaveWorkspace: () => Promise<boolean>
  onWorkspaceChange: () => void
}

export function useRequiredFieldsWorkspaceNavigation(
  args: UseRequiredFieldsWorkspaceNavigationArgs,
) {
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null)
  const [userCollapsedWorkspace, setUserCollapsedWorkspace] = useState(false)
  const [dialogFieldId, setDialogFieldId] = useState<string | null>(null)
  const [workspaceMode, setWorkspaceMode] = useState<IdentityWorkspaceMode | null>(
    null,
  )

  useEffect(() => {
    setActiveFieldId(null)
    setUserCollapsedWorkspace(false)
    setWorkspaceMode(null)
  }, [args.sessionId])

  useEffect(() => {
    if (activeFieldId != null) return
    if (userCollapsedWorkspace) return
    if (!args.initialFieldId) return
    if (!args.fields.some((field) => field.canonicalId === args.initialFieldId)) {
      return
    }
    const initialField = args.fields.find(
      (field) => field.canonicalId === args.initialFieldId,
    )
    if (!initialField) return
    // Do not auto-open when every row is already satisfied (avoids jumping back
    // to e.g. Age after completion while shell.initialFieldId is still stale).
    if (
      args.fields.length > 0 &&
      args.fields.every((field) => isIdentityFieldReady(field))
    ) {
      return
    }
    if (isIdentityFieldReady(initialField)) return
    setActiveFieldId(args.initialFieldId)
    setWorkspaceMode(
      resolveInitialWorkspaceMode(initialField, args.initialWorkspaceMode ?? null),
    )
  }, [
    activeFieldId,
    args.fields,
    args.initialFieldId,
    args.initialWorkspaceMode,
    userCollapsedWorkspace,
  ])

  // Keep activeFieldId valid when fields change (clear if no longer present).
  useEffect(() => {
    if (!activeFieldId) return
    if (!args.fields.some((field) => field.canonicalId === activeFieldId)) {
      setActiveFieldId(null)
    }
  }, [activeFieldId, args.fields])

  /**
   * Collapse the currently-expanded field without running the dirty guard.
   * Draft state is preserved — re-expanding the same field restores the edit.
   */
  const collapseField = useCallback(() => {
    setUserCollapsedWorkspace(true)
    setActiveFieldId(null)
    setWorkspaceMode(null)
    setDialogFieldId(null)
  }, [])

  /**
   * Switch to a different field. Opens the async DiscardWorkspaceEditsDialog if
   * there are unsaved edits; navigation only proceeds if the user confirms.
   */
  const focusField = useCallback(
    async (fieldId: string, nextMode?: IdentityWorkspaceMode) => {
      if (!(await args.confirmLeaveWorkspace())) {
        return
      }
      args.onWorkspaceChange()
      if (dialogFieldId === fieldId) {
        setDialogFieldId(null)
        return
      }
      setActiveFieldId(fieldId)
      setWorkspaceMode(nextMode ?? null)
      setDialogFieldId(fieldId)
    },
    [args, dialogFieldId],
  )

  const handleDialogOpenChange = useCallback(
    async (open: boolean) => {
      if (open) {
        return
      }
      if (!(await args.confirmLeaveWorkspace())) {
        return
      }
      setDialogFieldId(null)
    },
    [args],
  )

  /**
   * Switch the workspace mode tab (Map to question / Set constant / Custom distribution).
   * Runs the dirty guard so in-progress edits in the current mode aren't silently lost.
   */
  const focusMode = useCallback(
    async (nextMode: IdentityWorkspaceMode) => {
      if (!activeFieldId || nextMode === workspaceMode) {
        return
      }
      if (!(await args.confirmLeaveWorkspace())) {
        return
      }
      args.onWorkspaceChange()
      setWorkspaceMode(nextMode)
    },
    [args, activeFieldId, workspaceMode],
  )

  const applyWorkspaceSelection = useCallback(
    (nextFieldId: string, nextMode: IdentityWorkspaceMode | null) => {
      args.onWorkspaceChange()
      setUserCollapsedWorkspace(false)
      setActiveFieldId(nextFieldId)
      setWorkspaceMode(nextMode)
      setDialogFieldId(nextFieldId)
    },
    [args],
  )

  /**
   * Programmatic close (e.g. identity step complete). Sets userCollapsedWorkspace
   * so the initialFieldId seeding effect does not immediately re-open a field—
   * especially stale std_dem_age while all rows are already Ready.
   * New sessions reset userCollapsedWorkspace via the sessionId effect.
   */
  const clearWorkspaceSelection = useCallback(() => {
    setUserCollapsedWorkspace(true)
    setActiveFieldId(null)
    setWorkspaceMode(null)
    setDialogFieldId(null)
  }, [])

  const prefetchWorkspaceSelection = useCallback(
    (_nextFieldId: string, _nextMode: IdentityWorkspaceMode | null) => {
      // Intentional noop: SWR caches warm reactively when
      // applyWorkspaceSelection sets the active field/mode state.
    },
    [],
  )

  return {
    activeFieldId,
    dialogFieldId,
    workspaceMode,
    setWorkspaceMode,
    collapseField,
    focusField,
    focusMode,
    handleDialogOpenChange,
    applyWorkspaceSelection,
    prefetchWorkspaceSelection,
    clearWorkspaceSelection,
  }
}
